import { pipe } from 'fp-ts/function';
import { array } from 'fp-ts';
import { useState } from 'react';
import { Stack, Tab, Tabs, Title } from '@optimus-web/core';
import { lt } from '@mews/framework';
import { DefaultErrorHandler } from '@mews-commander/ui';
import {
    ActorProfilesSection,
    ModalSidePanel,
    SettlementRuleDetail,
    SettlementRuleName,
} from '../../../../../components';
import { useCentralPaymentPoliciesGet } from '../../queries';
import { HeaderActions } from './HeaderActions';
import { PropertiesTabContent } from './PropertiesTabContent';
import { EditPaymentPolicyButton } from './EditPaymentPolicyButton';

interface PaymentPoliciesSidePanelProps {
    paymentPolicyId?: string;
    handleCloseSidePanel: () => void;
}

export const PaymentPoliciesSidePanel = ({ paymentPolicyId, handleCloseSidePanel }: PaymentPoliciesSidePanelProps) => {
    const [activeTab, setActiveTab] = useState<'details' | 'properties'>('details');

    const { data, isFetching, isError } = useCentralPaymentPoliciesGet(
        { centralPaymentPolicyId: paymentPolicyId ?? '' },
        { enabled: !!paymentPolicyId }
    );

    const content =
        paymentPolicyId && data?.centralPaymentPolicy ? (
            <Stack vertical spacing="400">
                <Tabs
                    activeTabValue={activeTab}
                    inline
                    onTabClick={(_, value) => setActiveTab(value as 'details' | 'properties')}
                    showDivider
                >
                    <Tab id="details" label={lt('DetailPlural')} value="details" />
                    <Tab id="properties" label={lt('Properties')} value="properties" />
                </Tabs>
                {activeTab === 'details' && data.centralPaymentPolicy ? (
                    <Stack vertical spacing="400">
                        <SettlementRuleDetail
                            {...data.centralPaymentPolicy}
                            key={undefined}
                            policyKey={data.centralPaymentPolicy.key}
                            rightContent={<EditPaymentPolicyButton paymentPolicyId={paymentPolicyId} />}
                            isPortfolioManaged
                        />
                        <ActorProfilesSection
                            createdUtc={data.centralPaymentPolicy.createdUtc}
                            creatorProfile={{ name: data.centralPaymentPolicy.creatorProfileName }}
                            updatedUtc={data.centralPaymentPolicy.updatedUtc}
                            updaterProfile={{ name: data.centralPaymentPolicy.updaterProfileName }}
                        />
                    </Stack>
                ) : null}
                {activeTab === 'properties' ? <PropertiesTabContent data={data.centralPaymentPolicy} /> : null}
            </Stack>
        ) : null;

    return (
        <ModalSidePanel
            content={isError ? <DefaultErrorHandler /> : content}
            headerActions={
                paymentPolicyId ? (
                    <HeaderActions
                        handleCloseSidePanel={handleCloseSidePanel}
                        paymentPolicyId={paymentPolicyId}
                        isAssignedToRateGroups={
                            data
                                ? pipe(
                                      [
                                          ...(data?.centralPaymentPolicy?.propertyRateGroupCounts ?? []),
                                          ...(data?.centralPaymentPolicy?.propertyGroupRateGroupCounts ?? []),
                                      ],
                                      array.some(({ rateGroupCount }) => rateGroupCount > 0)
                                  )
                                : false
                        }
                    />
                ) : null
            }
            loading={isFetching}
            onClose={handleCloseSidePanel}
            opened={!!paymentPolicyId}
            title={
                data?.centralPaymentPolicy && (
                    <Title level={2} wordBreak="break-all" ellipsis maxLines={2}>
                        <SettlementRuleName
                            {...data.centralPaymentPolicy}
                            value={{
                                discriminator: 'Percentage',
                                // @ts-expect-error Type 'undefined' is not assignable to type 'number'
                                value: { value: data.centralPaymentPolicy.percentageValue },
                            }}
                        />
                    </Title>
                )
            }
            width="medium"
        />
    );
};
